import React, { useEffect, useMemo, useState } from "react";
import { Copy, FileUp, Plus, Printer, Trash2 } from "lucide-react";
import { Modal } from "./common";
import { useLang } from "../i18n";
import api, { apiError } from "../api";
import { toast } from "sonner";

const newRow = (name = "") => ({
  id: crypto.randomUUID(),
  name,
  username: "",
  class_id: "",
});

export default function StudentImportModal({ open, onClose, onDone, classes = [] }) {
  const { t, pick } = useLang();
  const [fileName, setFileName] = useState("");
  const [columns, setColumns] = useState([]);
  const [sourceRows, setSourceRows] = useState([]);
  const [nameColumn, setNameColumn] = useState("");
  const [rows, setRows] = useState([]);
  const [sharedClassId, setSharedClassId] = useState("");
  const [createLogin, setCreateLogin] = useState(false);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);

  useEffect(() => {
    if (open) {
      setFileName("");
      setColumns([]);
      setSourceRows([]);
      setNameColumn("");
      setRows([]);
      setSharedClassId("");
      setCreateLogin(false);
      setErrors({});
      setResults(null);
    }
  }, [open]);

  const className = (classId) => {
    const currentClass = classes.find((item) => item.id === classId);
    return currentClass ? pick(currentClass, "name") : "—";
  };

  const rowsForColumn = (items, column) => items
    .map((item) => newRow(item.values[column] || ""))
    .filter((row) => row.name.trim());

  const upload = async (event) => {
    const uploadFile = event.target.files?.[0];
    if (!uploadFile) return;
    setBusy(true);
    try {
      const formData = new FormData();
      formData.append("file", uploadFile);
      const { data } = await api.post("/staff/students/import/parse", formData);
      const firstColumn = data.suggested_columns[0] || data.columns[0];
      setFileName(data.filename);
      setColumns(data.columns);
      setSourceRows(data.rows);
      setNameColumn(firstColumn);
      setRows(rowsForColumn(data.rows, firstColumn));
      setErrors({});
    } catch (error) {
      toast.error(apiError(error, t));
    } finally {
      event.target.value = "";
      setBusy(false);
    }
  };

  const changeNameColumn = (column) => {
    setNameColumn(column);
    setRows(rowsForColumn(sourceRows, column));
    setErrors({});
  };

  const updateRow = (id, key, value) => {
    setRows((current) => current.map((row) => (
      row.id === id ? { ...row, [key]: value } : row
    )));
    setErrors((current) => ({ ...current, [id]: { ...current[id], [key]: null } }));
  };

  const removeRow = (id) => setRows((current) => current.filter((row) => row.id !== id));

  const validate = () => {
    const nextErrors = {};
    const names = new Map();
    const usernames = new Map();
    rows.forEach((row) => {
      const name = row.name.trim();
      const username = row.username.trim().toLowerCase();
      const normalizedName = name.replace(/\s+/g, " ").toLocaleLowerCase();
      if (!name) {
        nextErrors[row.id] = { ...nextErrors[row.id], name: t("validation.required") };
      } else if (names.has(normalizedName)) {
        const firstId = names.get(normalizedName);
        nextErrors[row.id] = { ...nextErrors[row.id], name: t("teacher.import_duplicate_name") };
        nextErrors[firstId] = {
          ...nextErrors[firstId],
          name: t("teacher.import_duplicate_name"),
        };
      } else {
        names.set(normalizedName, row.id);
      }
      if (createLogin && username && !/^[a-zA-Z0-9_.@-]{3,50}$/.test(username)) {
        nextErrors[row.id] = {
          ...nextErrors[row.id],
          username: t("validation.username_invalid"),
        };
      } else if (createLogin && username && usernames.has(username)) {
        const firstId = usernames.get(username);
        nextErrors[row.id] = {
          ...nextErrors[row.id],
          username: t("teacher.bulk_username_duplicate"),
        };
        nextErrors[firstId] = {
          ...nextErrors[firstId],
          username: t("teacher.bulk_username_duplicate"),
        };
      } else if (createLogin && username) {
        usernames.set(username, row.id);
      }
    });
    if (!rows.length) nextErrors.empty = { name: t("teacher.import_no_students") };
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const showServerErrors = (serverErrors) => {
    const nextErrors = {};
    serverErrors.forEach(({ index, field, code }) => {
      const row = rows[index];
      if (!row) return;
      const key = field === "name" ? "name" : "username";
      const message = code === "name_duplicate"
        ? t("teacher.import_duplicate_name")
        : code === "username_duplicate"
          ? t("teacher.bulk_username_duplicate")
          : t(`validation.${code}`);
      nextErrors[row.id] = { ...nextErrors[row.id], [key]: message };
    });
    setErrors(nextErrors);
  };

  const submit = async () => {
    if (!validate()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/staff/students/bulk", {
        auto_generate_usernames: createLogin,
        create_login: createLogin,
        class_id: sharedClassId || null,
        students: rows.map((row) => ({
          name_ar: row.name.trim(),
          username: row.username.trim().toLowerCase(),
          class_id: row.class_id || null,
        })),
      });
      setResults(data.results);
      toast.success(t("teacher.import_created", { n: data.results.length }));
      onDone?.();
    } catch (error) {
      const serverErrors = error.response?.data?.detail?.errors;
      if (Array.isArray(serverErrors)) {
        showServerErrors(serverErrors);
        toast.error(t("teacher.bulk_fix_errors"));
      } else {
        toast.error(apiError(error, t));
      }
    } finally {
      setBusy(false);
    }
  };

  const credentials = useMemo(
    () => (results || []).filter((student) => student.login_enabled).map((student) => [
      student.name_ar,
      `${student.username} / ${student.temp_password}`,
      className(student.class_id),
    ].join("\n")).join("\n\n"),
    [results, classes]
  );

  const copyAll = async () => {
    try {
      await navigator.clipboard.writeText(credentials);
      toast.success(t("teacher.bulk_credentials_copied"));
    } catch {
      toast.error(t("common.error_generic"));
    }
  };

  return (
    <Modal open={open} onClose={() => !busy && onClose()} title={t("teacher.import_students")}
      testId="student-import-modal" wide>
      {results ? (
        <div className="space-y-4">
          <p data-testid="import-created-summary" className="text-lg font-extrabold text-[#48D276]">
            {t("teacher.import_created", { n: results.length })}
          </p>
          <div className="overflow-x-auto rounded-2xl border border-[#FFC83D]/30">
            <table data-testid="import-credentials-table" className="w-full text-sm" dir="auto">
              <thead className="bg-[#0D0B38]/70 text-[#A5A0D6]">
                <tr>
                  <th className="p-3 text-start">{t("common.name")}</th>
                  {createLogin && <th className="p-3 text-start">{t("teacher.username")}</th>}
                  {createLogin && <th className="p-3 text-start">{t("teacher.temp_password")}</th>}
                  <th className="p-3 text-start">{t("teacher.class_label")}</th>
                </tr>
              </thead>
              <tbody>
                {results.map((student) => (
                  <tr key={student.id} data-testid={`import-credential-${student.username}`}
                    className="border-t border-white/10">
                    <td className="p-3 font-bold">{pick(student, "name")}</td>
                    {createLogin && <td className="p-3 font-bold" dir="ltr">{student.username}</td>}
                    {createLogin && (
                      <td className="p-3 font-black text-[#FFC83D]" dir="ltr">
                        {student.temp_password}
                      </td>
                    )}
                    <td className="p-3">{className(student.class_id)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {createLogin && <div className="flex flex-wrap gap-3">
            <button data-testid="copy-import-credentials-btn" onClick={copyAll}
              className="btn-magic flex-1">
              <Copy className="w-4 h-4" /> {t("teacher.copy_all_credentials")}
            </button>
            <button data-testid="print-import-credentials-btn" onClick={() => window.print()}
              className="btn-magic btn-ghost">
              <Printer className="w-4 h-4" /> {t("teacher.print_credentials")}
            </button>
          </div>}
          <button data-testid="student-import-done-btn" onClick={onClose}
            className="btn-magic w-full">
            {t("common.close")}
          </button>
        </div>
      ) : !sourceRows.length ? (
        <div className="space-y-4">
          <label data-testid="student-import-upload-label"
            className="block border-2 border-dashed border-[#27C9EE]/45 rounded-2xl p-8 text-center
              cursor-pointer hover:border-[#27C9EE] transition-colors">
            <FileUp className="w-8 h-8 mx-auto mb-3 text-[#27C9EE]" />
            <span className="font-extrabold block">{t("teacher.import_upload")}</span>
            <span className="text-xs text-[#A5A0D6] mt-1 block">.csv, .xlsx, .xls</span>
            <input data-testid="student-import-file-input" type="file" accept=".csv,.xlsx,.xls"
              className="sr-only" onChange={upload} disabled={busy} />
          </label>
          {busy && (
            <p data-testid="student-import-loading" className="text-center">
              {t("common.loading")}
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          <p data-testid="student-import-file-name" className="text-sm font-bold text-[#A5A0D6]">
            {fileName}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-extrabold block mb-1">
                {t("teacher.import_name_column")}
              </label>
              <select data-testid="import-name-column-select" className="input-magic"
                value={nameColumn}
                onChange={(event) => changeNameColumn(event.target.value)}>
                {columns.map((column) => (
                  <option key={column} value={column}>{column}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-extrabold block mb-1">
                {t("teacher.bulk_shared_class")}
              </label>
              <select data-testid="import-shared-class-select" className="input-magic"
                value={sharedClassId}
                onChange={(event) => setSharedClassId(event.target.value)}>
                <option value="" label={t("teacher.bulk_choose_per_row")} />
                {classes.map((item) => (
                  <option key={item.id} value={item.id} label={pick(item, "name")} />
                ))}
              </select>
            </div>
          </div>
          <label data-testid="import-create-login-toggle"
            className="flex items-center gap-2 text-sm font-extrabold">
            <input data-testid="import-create-login-checkbox" type="checkbox" checked={createLogin}
              onChange={(event) => setCreateLogin(event.target.checked)} />
            {t("teacher.create_login_now")}
          </label>
          {errors.empty && (
            <p data-testid="import-empty-error" className="text-sm text-[#FF8FB1]">
              {errors.empty.name}
            </p>
          )}
          <div className="max-h-[360px] overflow-y-auto space-y-3 pe-1">
            {rows.map((row, index) => (
              <div key={row.id} data-testid={`import-student-row-${index}`}
                className={`rounded-2xl border bg-[#0D0B38]/45 p-3 ${errors[row.id]
                  ? "border-[#F43F5E]/70"
                  : "border-white/10"}`}>
                <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_1fr_auto] gap-2
                  items-end">
                  <div>
                    <label className="text-xs font-bold text-[#D8D4F2] block mb-1">
                      {t("teacher.bulk_student_name")} *
                    </label>
                    <input data-testid={`import-student-name-${index}`} className="input-magic"
                      value={row.name}
                      onChange={(event) => updateRow(row.id, "name", event.target.value)} />
                    {errors[row.id]?.name && (
                      <p data-testid={`import-student-name-error-${index}`}
                        className="text-xs text-[#FF8FB1] mt-1">
                        {errors[row.id].name}
                      </p>
                    )}
                  </div>
                  {createLogin && (
                    <div>
                    <label className="text-xs font-bold text-[#D8D4F2] block mb-1">
                      {t("teacher.import_username_optional")}
                    </label>
                    <input data-testid={`import-student-username-${index}`}
                      className="input-magic" dir="ltr" value={row.username}
                      onChange={(event) => updateRow(row.id, "username", event.target.value)} />
                    {errors[row.id]?.username && (
                      <p data-testid={`import-student-username-error-${index}`}
                        className="text-xs text-[#FF8FB1] mt-1">
                        {errors[row.id].username}
                      </p>
                    )}
                    </div>
                  )}
                  <div>
                    <label className="text-xs font-bold text-[#D8D4F2] block mb-1">
                      {t("teacher.class_label")}
                    </label>
                    <select data-testid={`import-student-class-${index}`} className="input-magic"
                      disabled={Boolean(sharedClassId)}
                      value={sharedClassId || row.class_id}
                      onChange={(event) => updateRow(row.id, "class_id", event.target.value)}>
                      <option value="" label="—" />
                      {classes.map((item) => (
                        <option key={item.id} value={item.id} label={pick(item, "name")} />
                      ))}
                    </select>
                  </div>
                  <button data-testid={`remove-import-student-${index}`}
                    onClick={() => removeRow(row.id)}
                    title={t("teacher.bulk_remove_row")}
                    className="w-9 h-9 rounded-lg text-[#FF8FB1] hover:bg-[#F43F5E]/15">
                    <Trash2 className="w-4 h-4 mx-auto" />
                  </button>
                </div>
              </div>
            ))}
          </div>
          <button data-testid="add-import-student-row-btn"
            onClick={() => setRows((current) => [...current, newRow()])}
            className="btn-magic btn-ghost w-full">
            <Plus className="w-4 h-4" /> {t("teacher.bulk_add_row")}
          </button>
          <button data-testid="confirm-student-import-btn" disabled={busy} onClick={submit}
            className="btn-magic w-full">
            {busy ? t("common.loading") : t("teacher.import_confirm")}
          </button>
        </div>
      )}
    </Modal>
  );
}